import { useEffect, useMemo, useState } from 'react';
import { ThemeProvider, Toast } from '@gupshup-frontend/gupshup-ts-components';
import { getTemplates } from '../../api/gsIntg';
import { templateErrorMessage } from '../../api/templates';
import { enterpriseChannels, isMmLiteChannel } from '../../helper/channels';
import { DEFAULT_STATUS_FILTER } from '../../helper/templateStatus';
import { GUPSHUP_THEME_CONFIG } from '../../utils/constants';
import List from './List';
import DeleteModal from './DeleteModal';
import DetailModal from './DetailModal';
import TemplateForm from './Form';

// Tab root: owns channel/status/page selection, the fetched list, and loading/error state.
// List.js is presentational only — everything it needs arrives as props.
const Templates = ({ orgId, org }) => {
  // org only changes reference when WidgetShell actually re-fetches it (loadOrg) — memoized so
  // unrelated re-renders here (toast open/close, pendingAction changes) don't rebuild this array.
  const channels = useMemo(() => enterpriseChannels(org), [org]);

  const [selectedChannelId, setSelectedChannelId] = useState(() => channels[0]?.value ?? null);
  const selectedChannel = channels.find((channel) => channel.value === selectedChannelId);
  // TemplateForm's channel chip (console parity, C/index.tsx's AppNameDisplay) — the
  // currently-selected channel's own `label` (enterpriseChannels' "name · number"), not a
  // separate lookup, so it can never drift from what List.js's own channel picker just showed.
  const selectedChannelLabel = selectedChannel?.label;
  // Android deep link (Buttons.js) is offered only on an MM Lite number. Ops set the account's MM
  // Lite numbers via org.config.mmLiteNumbers (PUT /integrations/org/{id}) — never from this UI.
  // Passed to TemplateForm below, and from there into Buttons.js via sections/index.js's extraProps.
  const isMmLite = isMmLiteChannel(selectedChannel, org?.config?.mmLiteNumbers);
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS_FILTER);
  const [page, setPage] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, message: '', type: 'error' });
  // Bumped by handleDeleted, handleCreated and handleRefresh to force a refetch — see the fetch
  // effect's deps below.
  const [reloadToken, setReloadToken] = useState(0);

  // Seam for the create/view/edit/delete/clone intents: stashes which row + which intent.
  // 'delete' and 'view' are wired to DeleteModal and DetailModal below; 'create', 'edit' and
  // 'clone' all render TemplateForm (see the combined intent check below) — 'edit' passes the
  // real template row through `template` so the form prefills from it AND edits it in place;
  // 'clone' passes that same row through TemplateForm's separate `cloneSource` prop so the form
  // prefills from it too but submits as a brand new template instead.
  // { intent: 'create'|'view'|'edit'|'delete'|'clone', template }
  const [pendingAction, setPendingAction] = useState(null);

  // Shared by the list-load failure below and the onError callbacks handed to TemplateForm/
  // DeleteModal — a page-level toast so a failure is never left showing nowhere (e.g. the
  // submit-error Alert inside TemplateForm's tall preview modal, which can render off-screen).
  const showErrorToast = (message) => setToast({ open: true, type: 'error', message });

  // A new channel or status always starts back on page 1 of that result set.
  useEffect(() => {
    setPage(0);
  }, [selectedChannelId, statusFilter]);

  // Tabs unmounts inactive panels (see Widget/index.js), so this component remounts fresh on every
  // tab switch — that remount's initial run of this effect IS the "refetch on tab switch" behavior.
  // There is no list to cache across mounts.
  useEffect(() => {
    if (!selectedChannelId) return undefined;
    let cancelled = false;
    setLoading(true);
    getTemplates(orgId, selectedChannelId, statusFilter)
      .then((list) => {
        if (cancelled) return;
        setTemplates(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setTemplates([]);
        showErrorToast(templateErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // reloadToken is never read inside this effect — it exists purely so bumping it
    // (handleDeleted, handleCreated, handleRefresh) re-runs the fetch above without duplicating
    // that logic in each handler.
  }, [orgId, selectedChannelId, statusFilter, reloadToken]);

  if (channels.length === 0) {
    // The shell's onboarding banner already covers this state.
    return null;
  }

  // View detail — opens DetailModal below; reads pendingAction.template when intent === 'view'.
  const handleView = (template) => setPendingAction({ intent: 'view', template });
  // Edit — opens TemplateForm below prefilled from this row; reads pendingAction.template when
  // intent === 'edit'.
  const handleEdit = (template) => setPendingAction({ intent: 'edit', template });
  // Clone — opens TemplateForm below prefilled from this row, same as edit, but the form reads it
  // through the separate cloneSource prop (never `template`) so it submits as a brand new
  // template instead of updating the original — see the isFormView block below.
  const handleClone = (template) => setPendingAction({ intent: 'clone', template });
  // Delete — opens the confirm modal; reads pendingAction.template when intent === 'delete'.
  const handleDelete = (template) => setPendingAction({ intent: 'delete', template });
  // Create — opens TemplateForm below with no initial template.
  const handleCreate = () => setPendingAction({ intent: 'create', template: null });
  // Runs after DeleteModal's own delete call has actually succeeded — bumps reloadToken to
  // refetch the list and shows a success toast. pendingAction still holds the deleted row here;
  // DeleteModal calls onClose() (which clears it) only after this.
  const handleDeleted = () => {
    setReloadToken((prev) => prev + 1);
    const name = pendingAction?.template?.name;
    setToast({ open: true, type: 'success', message: name ? `"${name}" deleted.` : 'Template deleted.' });
  };
  // Runs after TemplateForm's own create/update call has actually succeeded — same reload+toast
  // shape as handleDeleted above. Shared by the 'create', 'edit' AND 'clone' intents (see the
  // combined intent check below), so all three get this refetch-and-toast behavior for free
  // instead of a duplicated handler — a clone's own submit is a plain create (Form/index.js never
  // sets isEdit for it), so the generic "Template created." message is already correct for it
  // too. Edit is the one that overwrites an existing template in place rather than adding a new
  // one, so it gets its own wording. No console copy to mirror here either way: gs-ui-templates'
  // services/template.ts shows the exact same 'template_submitted_successfully' toast on both its
  // create (postCreateTemplateRaw) and edit (putEditedTemplate) paths — there's no distinct
  // "updated" string there to adopt, so this keeps the short, our-own-style wording the other two
  // intents already use ("Template deleted." above). pendingAction still holds
  // {intent:'create'|'edit'|'clone'} here; TemplateForm calls onClose() (which clears it,
  // unmounting the form and returning to the list) only after this.
  const handleCreated = () => {
    setReloadToken((prev) => prev + 1);
    const message = pendingAction?.intent === 'edit' ? 'Template updated.' : 'Template created.';
    setToast({ open: true, type: 'success', message });
  };
  // Refresh button (List.js) — re-runs the same fetch effect via reloadToken, same as
  // handleDeleted/handleCreated above, but with no toast of its own: the list swap is the only
  // feedback this one needs.
  const handleRefresh = () => setReloadToken((prev) => prev + 1);

  // The create/edit/clone form is a full-page replacement for the list (matching the console's
  // own CreateTemplate, which replaces its template list view entirely), not a modal — so it's
  // mutually exclusive with List + the view/delete modals below, never layered on top of them.
  // Three intents share this one branch, differing only in which template (if any) they hand
  // TemplateForm and through which prop: 'edit' passes the real row through `template`, so the
  // form both prefills from it and edits it in place; 'clone' passes that same shape of row
  // through TemplateForm's separate `cloneSource` prop, so the form prefills from it too but
  // `template` stays null and it submits as a create; 'create' passes null to both. All three
  // reuse the exact same onClose/onSaved handlers, so edit and clone get the same
  // refetch-and-toast behavior without a duplicated block.
  const isFormView = ['create', 'edit', 'clone'].includes(pendingAction?.intent);

  return (
    <ThemeProvider config={GUPSHUP_THEME_CONFIG}>
      {isFormView ? (
        <TemplateForm
          orgId={orgId}
          channelId={selectedChannelId}
          channelLabel={selectedChannelLabel}
          isMmLite={isMmLite}
          template={pendingAction.intent === 'edit' ? pendingAction.template : null}
          cloneSource={pendingAction.intent === 'clone' ? pendingAction.template : null}
          onClose={() => setPendingAction(null)}
          onSaved={handleCreated}
          onError={showErrorToast}
        />
      ) : (
        <>
          <List
            channels={channels}
            selectedChannelId={selectedChannelId}
            onChannelChange={setSelectedChannelId}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            templates={templates}
            loading={loading}
            page={page}
            onPageChange={setPage}
            onRefresh={handleRefresh}
            onCreate={handleCreate}
            onView={handleView}
            onEdit={handleEdit}
            onClone={handleClone}
            onDelete={handleDelete}
          />

          {pendingAction?.intent === 'view' && (
            <DetailModal template={pendingAction.template} onClose={() => setPendingAction(null)} onEdit={handleEdit} />
          )}

          {pendingAction?.intent === 'delete' && (
            <DeleteModal
              orgId={orgId}
              channelId={selectedChannelId}
              template={pendingAction.template}
              onClose={() => setPendingAction(null)}
              onDeleted={handleDeleted}
              onError={showErrorToast}
            />
          )}
        </>
      )}

      <Toast
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        data-testid="bloomreach-templates-toast"
        maxWidth="30vw"
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        title={toast.message}
        type={toast.type}
        open={toast.open}
        autoHideDuration={toast.type === 'error' ? null : 5000}
      />
    </ThemeProvider>
  );
};

export default Templates;
